import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

// Material UI imports
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import ErrorIcon from '@mui/icons-material/Error';
import UpdateIcon from '@mui/icons-material/Update';
import AddIcon from '@mui/icons-material/Add';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Checkbox from '@mui/material/Checkbox';
import LinearProgress from '@mui/material/LinearProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

// General imports
import _ from 'lodash';
import axios from 'axios';
import Papa from 'papaparse';
import { hasUserDrivenActivity } from '../dashboard/utils/accounts';

const AddAccounts = (props) => {
    const { accesstoken } = useOutletContext();
    const [organization, setOrganization] = useState('');
    const [organizationId, setOrganizationId] = useState('');
    const [organizationValid, setOrganizationValid] = useState(null);
    const [action, setAction] = useState('upload');
    const [file, setFile] = useState(null);
    const [type, setType] = useState('accounts');
    const [ existing, setExisting ] = useState([]);
    const [ newAccounts, setNewAccounts ] = useState([]);
    const [ errors, setErrors ] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [results, setResults] = useState({});
    const [skipExisting, setSkipExisting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pipelineState, setPipelineState] = useState('Won');
    const [working, setWorking] = useState(false);
    const [orgData, setOrgData] = useState(null);

    const validateOrganization = _.debounce(async (org) => {
        if (!org) {
            setOrganizationValid(null);
            return;
        }

        try {
            const { data: _org } = await axios(`/api/organizations/${org}?accesstoken=${accesstoken}`);
            setOrganizationId(_org.id);
            setOrgData(_org);
            setOrganizationValid(true);
        } catch (error) {
            setOrganizationValid(false);
        }
    }, 500);

    const handleFileLoad = async () => {
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            const csvData = event.target.result;
            const { existingAccounts, newAccounts, errors } = await parseFile({ csvData, organizationId, accesstoken, isSupplier: type === 'suppliers' });
            setExisting(existingAccounts);
            setNewAccounts(newAccounts);
            setErrors(errors);
        };
        reader.readAsText(file);
    };

    const handleDownload = async () => {
        setWorking(true);
        try {
            const existing = await getFromServer({ type, accesstoken, organizationId });
            const filterEnabled = orgData?.settings?.accounts?.filterInactiveAccounts;
            const graceDays = orgData?.settings?.accounts?.filterGraceDays || 0;
            const graceMs = graceDays * 24 * 60 * 60 * 1000;
            const now = Date.now();

            // Build linked account IDs from opportunities and tradelines for accurate filter check
            let linkedAccountIds = new Set();
            if (filterEnabled && type === 'accounts') {
                try {
                    // Paginate through all opportunities
                    let oppCursor = null;
                    do {
                        const { data } = await axios(`/api/opportunities?organizationId=${organizationId}&accesstoken=${accesstoken}&since=0&cursor=${oppCursor || ''}`);
                        if (Array.isArray(data)) { data.forEach(o => { if (o.account?.id) linkedAccountIds.add(o.account.id); }); break; }
                        (data.opportunities || []).forEach(o => { if (o.account?.id) linkedAccountIds.add(o.account.id); });
                        oppCursor = data.cursor;
                    } while (oppCursor);
                    const { data: tradelines } = await axios(`/api/tradelines?organizationId=${organizationId}&accesstoken=${accesstoken}`);
                    (tradelines || []).forEach(t => { if (t.accountId) linkedAccountIds.add(t.accountId); });
                    console.log('[Admin] Linked account IDs from opps/tradelines:', linkedAccountIds.size);
                } catch (e) { console.error('[Admin] Error fetching opportunities/tradelines for filter check:', e); }
            }

            const csvObjects = existing.map(e => {
                let filteredOut = '';
                if (filterEnabled && type === 'accounts') {
                    const inGrace = graceMs > 0 && e.created && (now - e.created) < graceMs;
                    const passes = inGrace || hasUserDrivenActivity({ account: e, opportunityAccountIds: linkedAccountIds });
                    filteredOut = passes ? '' : 'Yes';
                }
                return {
                    'Unique ID': e.uniqueId?.split(':')[2],
                    'Branch Code': e.uniqueId?.split(':')[0],
                    'Branch Name': e.uniqueId?.split(':')[1],
                    'Company': e.name,
                    'Contact 1: First Name': e.contacts?.length > 0 ? e.contacts[0]?.firstName : '',
                    'Contact 1: Last Name': e.contacts?.length > 0 ? e.contacts[0]?.lastName : '',
                    'Contact 1: Title': e.contacts?.length > 0 ? e.contacts[0]?.title : '',
                    'Contact 1: Email': e.contacts?.length > 0 ? e.contacts[0]?.email : '',
                    'Contact 1: Phone': e.contacts?.length > 0 ? e.contacts[0]?.phone : '',
                    'Contact 2: First Name': e.contacts?.length > 1 ? e.contacts[1]?.firstName : '',
                    'Contact 2: Last Name': e.contacts?.length > 1 ? e.contacts[1]?.lastName : '',
                    'Contact 2: Title': e.contacts?.length > 1 ? e.contacts[1]?.title : '',
                    'Contact 2: Email': e.contacts?.length > 1 ? e.contacts[1]?.email : '',
                    'Contact 2: Phone': e.contacts?.length > 1 ? e.contacts[1]?.phone : '',
                    'Contact 3: First Name': e.contacts?.length > 2 ? e.contacts[2]?.firstName : '',
                    'Contact 3: Last Name': e.contacts?.length > 2 ? e.contacts[2]?.lastName : '',
                    'Contact 3: Title': e.contacts?.length > 2 ? e.contacts[2]?.title : '',
                    'Contact 3: Email': e.contacts?.length > 2 ? e.contacts[2]?.email : '',
                    'Contact 3: Phone': e.contacts?.length > 2 ? e.contacts[2]?.phone : '',
                    'Company Address': e.address?.text,
                    'Tags': e.tags?.map(t => t.label).filter(t => !t.startsWith('Pipeline:')).join(';'),
                    'Pipeline': (e.tags?.find(t => t.label.startsWith('Pipeline:'))?.label || ':').split(':')[1].trim(),
                    'Owner': e.owner,
                    ...(filterEnabled && type === 'accounts' ? { 'Filtered Out': filteredOut } : {}),
                };
            });
            const csvData = Papa.unparse(csvObjects);
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error downloading CSV:', error);
        } finally {
            setWorking(false);
        }
    };

    const handleAddAndUpdate = async () => {
        setProcessing(true);
        setProgress(0);

        const newResults = {};
        const totalOperations = skipExisting ? newAccounts.length : (existing.length + newAccounts.length);
        let completedOperations = 0;

        if (!skipExisting) {
            for (const account of existing) {
                newResults[`existing-${account.old.id}`] = 'processing';
            }
        }

        for (const account of newAccounts) {
            newResults[`new-${account.uniqueId}`] = 'processing';
        }

        setResults(newResults);

        if (!skipExisting) {
            for (const account of existing) {
                const id = `existing-${account.old.id}`;
                
                try {
                    await updateExisting({ account, isSupplier: type === 'suppliers', accesstoken, organizationId, pipelineState });
                    setResults(nr => ({ ...nr, [id]: 'success' }));
                } catch (error) {
                    setResults(nr => ({ ...nr, [id]: 'error' }));
                    console.error('Error updating account:', error);
                }
                completedOperations++;
                setProgress((completedOperations / totalOperations) * 100);
            }
        }

        for (const account of newAccounts) {
            const id = `new-${account.uniqueId}`;
            
            try {
                await addNew({ account, isSupplier: type === 'suppliers', accesstoken, organizationId, pipelineState });
                setResults(nr => ({ ...nr, [id]: 'success' }));
            } catch (error) {
                setResults(nr => ({ ...nr, [id]: 'error' }));
                console.error('Error adding account:', error);
            }
            completedOperations++;
            setProgress((completedOperations / totalOperations) * 100);
        }

        setProcessing(false);
    };

    console.log('[AddAccounts] newAccounts', newAccounts);

    return (
        <Box sx={{ mx: 'auto', p: 3 }}>
            <Typography variant='h5' gutterBottom>
                Accounts/Suppliers
            </Typography>

            <RadioGroup
                value={action}
                onChange={(e) => setAction(e.target.value)}
                row
            >
                <FormControlLabel value='upload' control={<Radio />} label='Upload CSV' />
                <FormControlLabel value='download' control={<Radio />} label='Download CSV' />
            </RadioGroup>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                    label='Organization'
                    value={organization}
                    onChange={(e) => { setOrganization(e.target.value); setOrganizationValid(null); }}
                    onBlur={() => validateOrganization(organization)}
                    margin='normal'
                    error={organizationValid === false}
                />
                {organizationValid === true && <CheckCircleIcon color="success" />}
                {organizationValid === false && <CancelIcon color="error" />}
            </Box>

            {action === 'upload' && (
                <>
                    <input
                        accept='.csv'
                        style={{ display: 'none' }}
                        id='csv-file-input'
                        type='file'
                        onChange={(e) => setFile(e.target.files[0])}
                        disabled={!organizationValid}
                    />
                    <label htmlFor='csv-file-input'>
                        <Button
                            variant='outlined'
                            component='span'
                            fullWidth
                            sx={{ my: 2 }}
                            disabled={!organizationValid}
                        >
                            Select CSV File
                        </Button>
                    </label>
                    {file && <Typography variant='body2'>{file.name}</Typography>}
                </>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl component='fieldset' sx={{ my: 2 }}>
                    <RadioGroup
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        row
                    >
                        <FormControlLabel
                            value='accounts'
                            control={<Radio />}
                            label='Accounts'
                        />
                        <FormControlLabel
                            value='suppliers'
                            control={<Radio />}
                            label='Suppliers'
                        />
                    </RadioGroup>
                </FormControl>
            </Box>
            <Button
                variant='contained'
                fullWidth
                onClick={action === 'upload' ? handleFileLoad : handleDownload}
                disabled={(!file || !organization || !organizationValid) && action === 'upload' || working}
            >
                {working ? '' : action === 'upload' ? 'Load File' : 'Download CSV'}
                {working && <CircularProgress size={20} />}
            </Button>

            {action === 'upload' && type === 'accounts' && (existing.length > 0 || newAccounts.length > 0) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <Typography>Pipeline stage:</Typography>
                    <FormControl sx={{ minWidth: 120 }}>
                        <Select
                            value={pipelineState}
                            onChange={(e) => setPipelineState(e.target.value)}
                            size="small"
                        >
                            <MenuItem value="New">New</MenuItem>
                            <MenuItem value="Qualified">Qualified</MenuItem>
                            <MenuItem value="Discovery">Discovery</MenuItem>
                            <MenuItem value="Proposal">Proposal</MenuItem>
                            <MenuItem value="Won">Won</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}

            {action === 'upload' && (existing.length > 0 || newAccounts.length > 0) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant='contained'
                            onClick={handleAddAndUpdate}
                            disabled={processing}
                            color="success"
                        >
                            {skipExisting ? 'Add New' : `Add and Update ${type === 'suppliers' ? 'Suppliers' : 'Accounts'}`}
                        </Button>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={skipExisting}
                                    onChange={(e) => setSkipExisting(e.target.checked)}
                                />
                            }
                            label="Skip existing"
                        />
                    </Box>
                    {(processing || progress > 0) && (
                        <Box sx={{ width: '100%' }}>
                            <LinearProgress 
                                variant="determinate" 
                                value={processing ? progress : 100} 
                            />
                            <Typography variant="body2" color="text.secondary" align="center">
                                {Math.round(processing ? progress : 100)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                                {Object.values(results).filter(r => r === 'success').length} successful, {Object.values(results).filter(r => r === 'error').length} failed
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {action === 'upload' && (errors.length > 0 || existing.length > 0 || newAccounts.length > 0) && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>Results</Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Unique ID</TableCell>
                                    <TableCell>Company Name</TableCell>
                                    <TableCell>Address</TableCell>
                                    <TableCell>Contacts</TableCell>
                                    <TableCell>Tags</TableCell>
                                    <TableCell>Owner</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {errors.map((error, idx) => (
                                    <TableRow key={`error-${idx}`}>
                                        <TableCell>
                                            <ErrorIcon color="error" />
                                        </TableCell>
                                        <TableCell colSpan={5}>{error}</TableCell>
                                    </TableRow>
                                ))}
                                {existing.map((account, idx) => (
                                    <TableRow key={`existing-${account.old.id}`}>
                                        <TableCell>
                                            {results[`existing-${account.old.id}`] === 'processing' ? (
                                                <CircularProgress size={20} />
                                            ) : results[`existing-${account.old.id}`] === 'success' ? (
                                                <CheckCircleIcon color="success" />
                                            ) : results[`existing-${account.old.id}`] === 'error' ? (
                                                <CancelIcon color="error" />
                                            ) : (
                                                <UpdateIcon color="warning" />
                                            )}
                                        </TableCell>
                                        <TableCell>{`${account.new.branch || ''}:${account.new.branchName || ''}:${account.new.uniqueId || ''}`}</TableCell>
                                        <TableCell>{account.new.companyName}</TableCell>
                                        <TableCell>{account.new.address}</TableCell>
                                        <TableCell>{account.new.contacts.filter(c => c.firstName || c.lastName || c.email || c.phone).length}</TableCell>
                                        <TableCell>{account.new.tags?.join(', ')}</TableCell>
                                        <TableCell>{account.new.owner}</TableCell>
                                    </TableRow>
                                ))}
                                {newAccounts.map((account, idx) => (
                                    <TableRow key={`new-${account.uniqueId}`}>
                                        <TableCell>
                                            {results[`new-${account.uniqueId}`] === 'processing' ? (
                                                <CircularProgress size={20} />
                                            ) : results[`new-${account.uniqueId}`] === 'success' ? (
                                                <CheckCircleIcon color="success" />
                                            ) : results[`new-${account.uniqueId}`] === 'error' ? (
                                                <CancelIcon color="error" />
                                            ) : (
                                                <AddIcon color="success" />
                                            )}
                                        </TableCell>
                                        <TableCell>{`${account.branch || ''}:${account.branchName || ''}:${account.uniqueId || ''}`}</TableCell>
                                        <TableCell>{account.companyName}</TableCell>
                                        <TableCell>{account.address}</TableCell>
                                        <TableCell>{account.contacts.filter(c => c.firstName || c.lastName || c.email || c.phone).length}</TableCell>
                                        <TableCell>{account.tags?.length || 0} {account.tags?.length > 0 && `(${account.tags?.join(', ')})`}</TableCell>
                                        <TableCell>{account.owner}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
};

export default AddAccounts;

async function parseFile({ csvData, organizationId, accesstoken, isSupplier }) {
    console.log('[AddAccounts] Parsing file', csvData, organizationId, accesstoken, isSupplier);
    const { data: parsedCsv } = Papa.parse(csvData, { header: true });
    const existingAccounts = [];
    const newAccounts = [];
    const errors = [];

    const existing = await getFromServer({ type: isSupplier ? 'suppliers' : 'accounts', accesstoken, organizationId });

    while (parsedCsv.length > 0) {

        const { 
            uniqueId, branch, branchName, companyName, contacts, address, tags, owner
        } = csvToObject(parsedCsv.shift());
        console.log('[AddAccounts] Parsing row', uniqueId, branch, branchName, companyName, contacts, address, tags, owner);

        const prev = existing.find(e => e.uniqueId === `${branch || ''}:${branchName || ''}:${uniqueId || ''}` && e.name === companyName);

        if (prev) {
            existingAccounts.push({ 
                new: {
                    uniqueId, branch, branchName, companyName, contacts, address, tags, owner
                },
                old: prev
            })
        } else {
            newAccounts.push({
                uniqueId, branch, branchName, companyName, contacts, address, tags, owner
            })
        }
    }

    return { existingAccounts, newAccounts, errors };
}

function csvToObject(csv) {
    const { 
        'Unique ID': uniqueId,
        'Branch Code': branch,
        'Branch Name': branchName,
        'Company': companyName,
        'Contact 1: First Name': firstName,
        'Contact 1: Last Name': lastName,
        'Contact 1: Title': title,
        'Contact 1: Email': email,
        'Contact 1: Phone': phone,
        'Contact 2: First Name': firstName2,
        'Contact 2: Last Name': lastName2,
        'Contact 2: Title': title2,
        'Contact 2: Phone': phone2,
        'Contact 2: Email': email2,
        'Contact 3: First Name': firstName3,
        'Contact 3: Last Name': lastName3,
        'Contact 3: Title': title3,
        'Contact 3: Phone': phone3,
        'Contact 3: Email': email3,
        'Company Address': address,
        'Tags': tags,
        'Owner': owner
    } = csv;
    return { 
        branch, 
        branchName, 
        uniqueId, 
        companyName: _.trim(companyName), 
        contacts: [{
            firstName, 
            lastName, 
            title, 
            email: _.trim(email), 
            phone
        }, {
            firstName: firstName2, 
            lastName: lastName2, 
            title: title2, 
            email: _.trim(email2), 
            phone: phone2
        }, {
            firstName: firstName3, 
            lastName: lastName3, 
            title: title3, 
            email: _.trim(email3), 
            phone: phone3
        }], 
        address, 
        tags: tags ? tags.split(';') : [],
        owner
    };
}

async function updateExisting({ account, isSupplier, accesstoken, organizationId, pipelineState }) {
    const { old, new: newAccount } = account;
    const { uniqueId, branch, branchName, companyName, contacts, address, tags, owner } = newAccount;
    const { id } = old;

    const addressObj = old.address.text === address ? old.address : await getAddress({ text: address });

    const updatedAccount = {
        uniqueId: `${branch || ''}:${branchName || ''}:${uniqueId || ''}`,
        contacts: contacts.map(c => _.pickBy(c, p => !!p)).filter(c => !_.isEmpty(c)),
        name: companyName,
        address: addressObj,
        tags: !isSupplier ? [{ label: `Pipeline: ${pipelineState}` }] : [],
        owner
    };

    updatedAccount.tags = [...updatedAccount.tags, ...tags.map(t => ({ label: t }))];

    const url = `https://app.monada.ai/api/${isSupplier ? 'suppliers' : 'accounts'}/${id}?organizationId=${organizationId}&accesstoken=${accesstoken}`;
    await axios.put(url, { [isSupplier ? 'supplier' : 'account']: updatedAccount });
}

async function addNew({ account, isSupplier, accesstoken, organizationId, pipelineState }) {
    const { uniqueId, branch, branchName, companyName, contacts, address, tags, owner } = account;
    const addressObj = await getAddress({ text: address });

    const newAccount = {
        uniqueId: `${branch || ''}:${branchName || ''}:${uniqueId || ''}`,
        contacts: contacts.map(c => _.pickBy(c, p => !!p)).filter(c => !_.isEmpty(c)),
        name: companyName,
        address: addressObj,
        tags: !isSupplier ? [{ label: `Pipeline: ${pipelineState}` }] : [],
        owner
    };

    newAccount.tags = [...newAccount.tags, ...tags.map(t => ({ label: t }))];

    const url = `https://app.monada.ai/api/${isSupplier ? 'suppliers' : 'accounts'}?organizationId=${organizationId}&accesstoken=${accesstoken}`;
    await axios.post(url, { [isSupplier ? 'supplier' : 'account']: newAccount });
}

async function getAddress({ text }) {
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    const place = await new Promise((resolve, reject) => {
        if (!text) {
            resolve(null);
            return;
        }
        service.textSearch({
            query: text
        }, (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                resolve(null);
                return;
            }
            if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
                console.error('Places API error:', status);
                reject(new Error(`Places API error: ${status}`));
                return;
            }

            // Get details for the first result
            service.getDetails({
                placeId: results[0].place_id,
                fields: ['name', 'address_components', 'types']
            }, (place, detailStatus) => {
                if (detailStatus !== window.google.maps.places.PlacesServiceStatus.OK) {
                    console.error('Places Details API error:', detailStatus);
                    reject(new Error(`Places Details API error: ${detailStatus}`));
                    return;
                }
                resolve({ ...place, id: results[0].place_id });
            });
        });
    });

    const addressObj = place ? {
        type: 'geocode',
        text,
        id: place.id,
        countryCode: place.address_components.find(c => c.types.includes('country'))?.short_name || 'NA'
    } : null;

    return addressObj;
}

async function getFromServer({ type, accesstoken, organizationId }) {

    const latest = 0;

    const items = [];
    let cursor = null;
    console.log(`[AddAccounts: getFromServer] Starting /api/${type === 'suppliers' ? 'suppliers' : 'accounts'}?organizationId=${organizationId}&since=${latest}&cursor=${cursor ? cursor : ''}`);
    do {
        const { data } = await axios(`https://app.monada.ai/api/${type === 'suppliers' ? 'suppliers' : 'accounts'}?organizationId=${organizationId}&accesstoken=${accesstoken}&since=${latest}&cursor=${cursor ? cursor : ''}`);
        if (_.isArray(data)) return data;
        cursor = data.cursor;
        items.push(...data[type === 'suppliers' ? 'suppliers' : 'accounts']);
        console.log(`[AddAccounts: getFromServer] Read ${items.length} items. Has more? ${cursor ? 'Yes' : 'No'}`);
    } while (cursor);

    return items;
}